import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  X,
  CalendarX2,
  Clock,
  AlarmMinus,
  Loader2,
  CheckCircle2,
  Info,
  Calendar,
  Sun,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";
import { patchProviderUnavailability } from "../../services/providerService";

interface Props {
  providerId: number;
  onClose: () => void;
  onSuccess?: () => void;
}

interface FormState {
  from_date: string;
  to_date: string;
  from_time: string;
  to_time: string;
  reason: string;
  is_full_day: boolean;
}

const REASON_PRESETS = [
  "Personal leave",
  "Medical conference",
  "Emergency",
  "Public holiday",
  "Training / CME",
  "Vacation",
];

const ProviderUnavailabilityModal: React.FC<Props> = ({
  providerId,
  onClose,
  onSuccess,
}) => {
  const today = new Date().toISOString().split("T")[0];

  const [form, setForm] = useState<FormState>({
    from_date: today,
    to_date: today,
    from_time: "09:00",
    to_time: "17:00",
    reason: "",
    is_full_day: false,
  });

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const toggleFullDay = () => {
    setForm((prev) => ({
      ...prev,
      is_full_day: !prev.is_full_day,
      from_time: !prev.is_full_day ? "00:00" : "09:00",
      to_time: !prev.is_full_day ? "23:59" : "17:00",
    }));
  };

  const validate = (): string | null => {
    if (!form.from_date) return "Please select a start date.";
    if (!form.to_date) return "Please select an end date.";
    if (form.to_date < form.from_date)
      return "End date cannot be before start date.";
    if (!form.is_full_day) {
      if (!form.from_time) return "Please select a start time.";
      if (!form.to_time) return "Please select an end time.";
      if (
        form.from_date === form.to_date &&
        form.to_time <= form.from_time
      )
        return "End time must be after start time.";
    }
    if (!form.reason.trim()) return "Please provide a reason.";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSubmitting(true);
      await patchProviderUnavailability(providerId, form);
      setSuccess(true);
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(
        err?.message ||
          "Failed to submit unavailability. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    // Backdrop
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(6px)" }}
      onClick={(e: React.MouseEvent<HTMLDivElement>) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ duration: 0.25 }}
        className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-xl overflow-hidden"
        onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-slate-900 px-8 pt-8 pb-6 relative overflow-hidden">
          <CalendarX2
            size={120}
            className="absolute -right-6 -bottom-6 text-white/5 pointer-events-none"
          />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2.5 bg-rose-500/20 rounded-xl inline-flex">
                <AlarmMinus size={22} className="text-rose-400" />
              </div>
              <button
                onClick={onClose}
                className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all"
              >
                <X size={18} />
              </button>
            </div>
            <h2 className="text-2xl font-black text-white tracking-tight mb-1">
              Mark Unavailability
            </h2>
            <p className="text-white/50 text-sm font-medium">
              Block consultation slots for a custom date &amp; time range.
            </p>
          </div>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          {success ? (
            <div className="flex flex-col items-center py-8 text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-4">
                <CheckCircle2 size={32} className="text-emerald-500" />
              </div>
              <h3 className="text-xl font-black text-slate-900 mb-1">
                Unavailability Saved
              </h3>
              <p className="text-slate-400 text-sm font-medium">
                Your schedule has been updated successfully.
              </p>
            </div>
          ) : (
            <>
              {/* Error Banner */}
              {error && (
                <div className="flex items-start gap-3 p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-700 text-sm font-medium">
                  <Info size={16} className="shrink-0 mt-0.5" />
                  {error}
                </div>
              )}

              {/* Full Day Toggle */}
              <div className="flex items-center justify-between p-5 rounded-2xl bg-slate-50 border border-slate-100">
                <div className="flex items-center gap-3">
                  <Sun size={18} className="text-amber-500" />
                  <div>
                    <p className="text-sm font-black text-slate-800 leading-none mb-0.5">
                      Full Day Block
                    </p>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                      Marks entire day(s) unavailable
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={toggleFullDay}
                  className="focus:outline-none"
                  aria-label="Toggle full day"
                >
                  {form.is_full_day ? (
                    <ToggleRight
                      size={36}
                      className="text-primary transition-colors"
                    />
                  ) : (
                    <ToggleLeft
                      size={36}
                      className="text-slate-300 transition-colors"
                    />
                  )}
                </button>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                    <Calendar size={12} /> From Date
                  </label>
                  <input
                    type="date"
                    value={form.from_date}
                    min={today}
                    onChange={(e) => set("from_date", e.target.value)}
                    className="w-full px-4 py-3 rounded-2xl bg-slate-50 border border-slate-200 text-slate-700 font-bold text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                    <Calendar size={12} /> To Date
                  </label>
                  <input
                    type="date"
                    value={form.to_date}
                    min={form.from_date}
                    onChange={(e) => set("to_date", e.target.value)}
                    className="w-full px-4 py-3 rounded-2xl bg-slate-50 border border-slate-200 text-slate-700 font-bold text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  />
                </div>
              </div>

              {/* Time Range – hidden when full-day */}
              {!form.is_full_day && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                      <Clock size={12} /> From Time
                    </label>
                    <input
                      type="time"
                      value={form.from_time}
                      onChange={(e) => set("from_time", e.target.value)}
                      className="w-full px-4 py-3 rounded-2xl bg-slate-50 border border-slate-200 text-slate-700 font-bold text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                      <Clock size={12} /> To Time
                    </label>
                    <input
                      type="time"
                      value={form.to_time}
                      onChange={(e) => set("to_time", e.target.value)}
                      className="w-full px-4 py-3 rounded-2xl bg-slate-50 border border-slate-200 text-slate-700 font-bold text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    />
                  </div>
                </div>
              )}

              {/* Reason Presets */}
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                  Reason
                </label>
                <div className="flex flex-wrap gap-2">
                  {REASON_PRESETS.map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => set("reason", preset)}
                      className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-all ${
                        form.reason === preset
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white text-slate-500 border-slate-200 hover:border-primary/30 hover:text-primary"
                      }`}
                    >
                      {preset}
                    </button>
                  ))}
                </div>
                <textarea
                  rows={2}
                  value={form.reason}
                  onChange={(e) => set("reason", e.target.value)}
                  placeholder="Or type a custom reason..."
                  className="w-full px-4 py-3 rounded-2xl bg-slate-50 border border-slate-200 text-slate-700 font-medium text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                />
              </div>

              {/* Summary Preview */}
              {form.from_date && form.reason && (
                <div className="p-4 rounded-2xl bg-primary/5 border border-primary/10 space-y-1.5">
                  <p className="text-[9px] font-black text-primary uppercase tracking-widest">
                    Unavailability Preview
                  </p>
                  <p className="text-sm font-bold text-slate-800">
                    📅{" "}
                    {form.from_date === form.to_date
                      ? form.from_date
                      : `${form.from_date} → ${form.to_date}`}
                    {!form.is_full_day &&
                      `   ⏱ ${form.from_time} – ${form.to_time}`}
                    {form.is_full_day && "  (Full Day)"}
                  </p>
                  <p className="text-xs text-slate-500 font-medium italic">
                    "{form.reason}"
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 h-12 rounded-2xl border border-slate-200 text-slate-500 font-bold text-sm hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-[2] h-12 rounded-2xl bg-rose-600 hover:bg-rose-700 text-white font-black text-sm shadow-lg shadow-rose-500/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Submitting…
                    </>
                  ) : (
                    <>
                      <AlarmMinus size={16} />
                      Mark Unavailable
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </form>
      </motion.div>
    </div>
  );
};

export default ProviderUnavailabilityModal;
